import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../styles/theme';
import { FaChartLine, FaShieldAlt, FaMobile, FaBatteryFull, FaTint, FaMapMarkedAlt } from 'react-icons/fa';
import ModernMarineLogo from '../assets/images/modern-marine-logo-white.png';

const LandingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  position: relative;
`;

const HeroSection = styled.section`
  padding: ${theme.spacing['4xl']} ${theme.spacing.lg};
  text-align: center;
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url(${ModernMarineLogo}) center/contain no-repeat;
    opacity: 0.08;
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: ${theme.typography.fontSize['6xl']};
  font-weight: ${theme.typography.fontWeight.extrabold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.1;
  
  .highlight {
    background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['4xl']};
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing['2xl']};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: ${theme.spacing.lg};
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.lg} ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  text-decoration: none;
  transition: ${theme.transitions.default};
  cursor: pointer;
  border: none;
  
  &.primary {
    background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.primary.dark});
    color: ${theme.colors.primary.contrast};
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: ${theme.shadows.xl};
    }
  }
  
  &.secondary {
    background: transparent;
    color: ${theme.colors.primary.light};
    border: 2px solid ${theme.colors.primary.main};
    
    &:hover {
      background: ${theme.colors.primary.main};
      color: ${theme.colors.primary.contrast};
      transform: translateY(-3px);
    }
  }
`;

const FeaturesSection = styled.section`
  padding: ${theme.spacing['4xl']} ${theme.spacing.lg};
  background: ${theme.colors.background.secondary};
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  text-align: center;
  margin-bottom: ${theme.spacing['2xl']};
  color: ${theme.colors.text.primary};
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing['3xl']};
`;

const FeatureCard = styled(motion.div)`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing['2xl']};
  backdrop-filter: blur(10px);
  transition: ${theme.transitions.default};
  
  &:hover {
    transform: translateY(-5px);
    border-color: ${theme.colors.primary.main};
    box-shadow: ${theme.shadows.xl};
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.lg};
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  font-size: ${theme.typography.fontSize.base};
`;

const PoweredBySection = styled(motion.div)`
  text-align: center;
  padding: ${theme.spacing['2xl']};
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius.xl};
  backdrop-filter: blur(10px);
  margin-top: ${theme.spacing['3xl']};
`;

const PoweredByText = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const PoweredByLink = styled.a`
  color: ${theme.colors.primary.light};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeight.semibold};
  
  &:hover {
    color: ${theme.colors.primary.main};
    text-decoration: underline;
  }
`;

const StatsSection = styled.section`
  padding: ${theme.spacing['4xl']} ${theme.spacing.lg};
  background: ${theme.colors.background.primary};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.xl};
  text-align: center;
`;

const StatItem = styled(motion.div)`
  padding: ${theme.spacing.lg};
`;

const StatNumber = styled.div`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.extrabold};
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.sm};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const LandingPage = ({ onGetStarted, onLogin }) => {
  const features = [
    {
      icon: <FaChartLine />,
      title: "Real-Time Monitoring",
      description: "Live data streaming from SignalK servers with instant updates on vessel status, navigation, and environmental conditions."
    },
    {
      icon: <FaBatteryFull />,
      title: "Electrical System Management",
      description: "Comprehensive battery monitoring, solar panel tracking, and electrical system diagnostics with predictive maintenance alerts."
    },
    {
      icon: <FaTint />,
      title: "Fluid Management",
      description: "Monitor fuel levels, water tanks, and bilge pumps with automated alerts and consumption tracking for optimal efficiency."
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Navigation & Tracking",
      description: "Advanced GPS tracking, route planning, and location sharing with real-time position updates and trip logging."
    },
    {
      icon: <FaShieldAlt />,
      title: "Safety & Security",
      description: "Comprehensive security monitoring with intrusion detection, anchor watch, and emergency alert systems."
    },
    {
      icon: <FaMobile />,
      title: "Mobile-First Design",
      description: "Responsive interface optimized for tablets and phones with critical monitoring accessible from anywhere on board."
    }
  ];

  return (
    <LandingContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Welcome to <span className="highlight">BoatWise</span>
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            The next-generation marine monitoring platform that brings your vessel's data to life. 
            Monitor, analyze, and optimize your boat's performance with real-time insights and intelligent alerts.
          </HeroSubtitle>
          
          <CTAButtons
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <CTAButton 
              className="primary"
              onClick={onGetStarted}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started Free
            </CTAButton>
            <CTAButton 
              className="secondary"
              onClick={onLogin}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Sign In
            </CTAButton>
          </CTAButtons>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <SectionContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Powerful Marine Technology
          </SectionTitle>
          
          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Built for modern boaters who demand reliability, performance, and peace of mind on the water.
          </SectionSubtitle>
          
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
          
          <PoweredBySection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <PoweredByText>
              Powered by <PoweredByLink href="https://www.modernmarine.ca" target="_blank" rel="noopener noreferrer">Modern Marine</PoweredByLink> - 
              Your trusted partner in marine technology solutions
            </PoweredByText>
          </PoweredBySection>
        </SectionContainer>
      </FeaturesSection>

      <StatsSection>
        <SectionContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Trusted by Boaters Worldwide
          </SectionTitle>
          
          <StatsGrid>
            <StatItem
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <StatNumber>500+</StatNumber>
              <StatLabel>Active Vessels</StatLabel>
            </StatItem>
            
            <StatItem
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <StatNumber>99.9%</StatNumber>
              <StatLabel>Uptime</StatLabel>
            </StatItem>
            
            <StatItem
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <StatNumber>24/7</StatNumber>
              <StatLabel>Monitoring</StatLabel>
            </StatItem>
            
            <StatItem
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <StatNumber>50+</StatNumber>
              <StatLabel>Data Points</StatLabel>
            </StatItem>
          </StatsGrid>
        </SectionContainer>
      </StatsSection>
    </LandingContainer>
  );
};

export default LandingPage;
